/**
 * Sidebar do Sistema MEDICARE
 * Navegação lateral para desktop
 */

import React from 'react';
import {
  Box,
  CloseButton,
  Flex,
  Text,
  useColorModeValue,
  BoxProps,
  VStack,
  Divider,
  Icon,
  Image,
} from '@chakra-ui/react';
import {
  FiHome,
  FiUsers,
  FiCalendar,
  FiMessageSquare,
  FiSettings,
  FiUser,
  FiLink,
  FiActivity,
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { NavItem } from './NavItem';

interface SidebarProps extends BoxProps {
  onClose: () => void;
}

// Configuração dos itens de navegação
const getNavItems = (userType: string) => {
  const baseItems = [
    { name: 'Dashboard', icon: FiHome, path: '/dashboard' },
    { name: 'Meu Perfil', icon: FiUser, path: '/perfil' },
  ];

  const consultasItems = [
    { name: 'Consultas', icon: FiCalendar, path: '/consultas' },
  ];

  const diagnosticoItems = [
    { name: 'Diagnóstico', icon: FiActivity, path: '/diagnostico' },
  ];

  const adminItems = [
    { name: 'Usuários', icon: FiUsers, path: '/usuarios' },
    { name: 'Endpoints', icon: FiLink, path: '/endpoints' },
    { name: 'Configurações', icon: FiSettings, path: '/configuracoes' },
  ];

  const medicoItems = [
    { name: 'Prompts IA', icon: FiMessageSquare, path: '/prompts' },
  ];

  const enfermeiroItems: Array<{ name: string; icon: any; path: string }> = [
    // Monitoramento removido conforme solicitado
  ];

  let items = [...baseItems];

  // Adicionar itens baseados no tipo de usuário
  if (['admin', 'medico', 'enfermeiro'].includes(userType)) {
    items = [...items, ...consultasItems];
  }

  // Adicionar Diagnóstico para médicos e administradores
  if (['admin', 'medico'].includes(userType)) {
    items = [...items, ...diagnosticoItems];
  }

  if (userType === 'paciente') {
    items.push({ name: 'Minhas Consultas', icon: FiCalendar, path: '/consultas' });
  }

  if (userType === 'admin') {
    items = [...items, ...adminItems];
  }

  // Prompts IA apenas para administradores (removido para médicos)
  if (userType === 'admin') {
    items = [...items, ...medicoItems];
  }

  if (['admin', 'enfermeiro'].includes(userType)) {
    items = [...items, ...enfermeiroItems];
  }

  return items;
};

export const Sidebar: React.FC<SidebarProps> = ({ onClose, ...rest }) => {
  const { usuario } = useAuth();
  const bgColor = useColorModeValue('white', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const navItems = getNavItems(usuario?.tipo_usuario || '');

  return (
    <Box
      transition="3s ease"
      bg={bgColor}
      borderRight="1px"
      borderRightColor={borderColor}
      w={{ base: 'full', md: 60 }}
      pos="fixed"
      h="full"
      zIndex="overlay"
      {...rest}
    >
      {/* Header da sidebar */}
      <Flex h="20" alignItems="center" mx="8" justifyContent="space-between">
        <Image
          src="/logo_medicare.png"
          alt="Medicare Logo"
          w={40}
          h={40}
          objectFit="contain"
        />
        <CloseButton display={{ base: 'flex', md: 'none' }} onClick={onClose} />
      </Flex>

      {/* Navegação */}
      <VStack spacing={1} align="stretch" px={4}>
        {navItems.map((item) => (
          <NavItem key={item.name} icon={item.icon} path={item.path}>
            {item.name}
          </NavItem>
        ))}
      </VStack>

      {/* Informações do usuário na parte inferior */}
      <Box position="absolute" bottom={4} left={4} right={4}>
        <Divider mb={4} />
        <Flex align="center" p={3} bg={useColorModeValue('gray.50', 'gray.800')} borderRadius="md">
          <Icon as={FiUser} mr={3} />
          <Box>
            <Text fontSize="sm" fontWeight="medium">
              {usuario?.nome}
            </Text>
            <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')}>
              {usuario?.email}
            </Text>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default Sidebar;